import { useState, useEffect } from 'react';
import { C } from '../styles/colours.js';
import socket from '../socket.js';
import { QRCodeSVG } from 'qrcode.react';
import { T, LANGUAGES } from '../translations.js';
import { getText } from '../questionBank.js';

const FREE_LIMIT = 6;

const GATE = {
  en: { reached: "Someone couldn't join", over: 'Free games are capped at 6 players. Go unlimited:', btn: 'Upgrade — unlimited players' },
  fr: { reached: "Quelqu'un n'a pas pu rejoindre", over: 'Les parties gratuites sont limitées à 6 joueurs. Passe à illimité :', btn: 'Passer à illimité' },
  ar: { reached: 'لم يتمكن أحد من الانضمام', over: 'الألعاب المجانية محدودة بـ 6 لاعبين. اذهب إلى غير محدود:', btn: 'ترقية — لاعبون بلا حدود' },
  es: { reached: 'Alguien no pudo unirse', over: 'Los juegos gratis están limitados a 6 jugadores. Hazte ilimitado:', btn: 'Mejorar — jugadores ilimitados' },
};

function generateRoomCode() {
  return Math.random().toString(36).substring(2, 6).toUpperCase();
}

function Host({ onBack, gameConfig, isPaid: isPaidProp, onUpgrade, lang = 'en' }) {
  const QUESTIONS = gameConfig?.questions || [];
  const displayLanguages = gameConfig?.displayLanguages || [lang];
  const t = T[lang] || T.en;
  const gate = GATE[lang] || GATE.en;
  const isRTL = lang === 'ar';

  const [qIndex, setQIndex] = useState(0);
  const [phase, setPhase] = useState('reading');
  const [buzzed, setBuzzed] = useState(null);
  const [buzzOrder, setBuzzOrder] = useState([]);
  const [result, setResult] = useState(null);
  const [players, setPlayers] = useState([]);
  const [roomCode] = useState(generateRoomCode);
  const [gameStarted, setGameStarted] = useState(false);
  const [winnerRevealed, setWinnerRevealed] = useState(false);
  const [limitReached, setLimitReached] = useState(false);

  const isPaid = isPaidProp || false;
  const currentQ = QUESTIONS[qIndex];
  const atLimit = !isPaid && players.length >= FREE_LIMIT;
  const showUpgradeNudge = !isPaid && limitReached && !gameStarted;

  useEffect(() => {
    socket.emit('create-room', { roomCode, questions: QUESTIONS, isPaid });

    socket.on('player-joined', (updatedPlayers) => setPlayers(updatedPlayers));
    socket.on('player-limit-reached', () => setLimitReached(true));
    socket.on('first-buzz', ({ name, buzzOrder: order }) => { setBuzzed({ name }); setBuzzOrder(order); });
    socket.on('buzz-update', (order) => setBuzzOrder(order));
    socket.on('answer-correct', ({ playerName, players: updatedPlayers }) => { setPlayers(updatedPlayers); setResult('correct'); });
    socket.on('next-in-line', (next) => { setBuzzed({ name: next.name }); setResult(null); });
    socket.on('buzzers-open', () => { setBuzzed(null); setBuzzOrder([]); setResult(null); });

    return () => {
      socket.off('player-joined');
      socket.off('player-limit-reached');
      socket.off('first-buzz');
      socket.off('buzz-update');
      socket.off('answer-correct');
      socket.off('next-in-line');
      socket.off('buzzers-open');
    };
  }, []);

  const openBuzzers = () => { setPhase('open'); socket.emit('open-buzzers', roomCode); };

  const validate = (correct) => {
    socket.emit('validate', { roomCode, correct, playerName: buzzed.name });
    if (correct) setResult('correct');
    else { setResult(null); setBuzzed(null); }
  };

  const nextQuestion = () => {
    if (qIndex >= QUESTIONS.length - 1) { setPhase('gameover'); return; }
    setQIndex(qIndex + 1);
    setPhase('reading');
    setBuzzed(null);
    setBuzzOrder([]);
    setResult(null);
    socket.emit('next-question', roomCode);
  };

  const revealWinner = () => {
    const winner = sortedPlayers[0];
    socket.emit('winner-revealed', { roomCode, winner });
    setWinnerRevealed(true);
  };

  const sortedPlayers = [...players].sort((a, b) => b.pts - a.pts);

  const renderMultiLang = (field, fontSize = 16) => {
    return displayLanguages.map(langCode => {
      const langInfo = LANGUAGES.find(l => l.code === langCode);
      const isLangRTL = langCode === 'ar';
      return (
        <div key={langCode} dir={isLangRTL ? 'rtl' : 'ltr'} style={{ fontSize, fontWeight: 700, lineHeight: 1.5, marginBottom: displayLanguages.length > 1 ? 10 : 0, textAlign: isLangRTL ? 'right' : 'left' }}>
          {displayLanguages.length > 1 && (
            <span style={{ fontSize: 13, opacity: 0.6, marginRight: isLangRTL ? 0 : 6, marginLeft: isLangRTL ? 6 : 0 }}>{langInfo?.flag}</span>
          )}
          {getText(field, langCode)}
        </div>
      );
    });
  };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ background: C.bg, minHeight: '100vh', fontFamily: 'sans-serif', color: C.white, padding: '20px', maxWidth: 480, margin: '0 auto' }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <button onClick={onBack} style={{ background: C.card, border: 'none', color: C.textSub, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>{t.back}</button>
        <div style={{ fontSize: 11, letterSpacing: 2, color: C.gold, fontWeight: 700 }}>{t.hostBadge}</div>
        {!isPaid && (
          <button onClick={() => onUpgrade && onUpgrade()} style={{ background: `${C.gold}20`, border: `1px solid ${C.gold}40`, color: C.gold, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
            {t.upgrade}
          </button>
        )}
        {isPaid && <div style={{ width: 70 }} />}
      </div>

      {gameConfig?.gameName && (
        <div style={{ fontSize: 18, fontWeight: 900, marginBottom: 14, color: C.white }}>{gameConfig.gameName}</div>
      )}

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: C.textMuted, marginBottom: 4 }}>{t.roomCode}</div>
            <div style={{ fontWeight: 900, fontSize: 28, letterSpacing: 6, color: C.gold }}>{roomCode}</div>
          </div>
          <div style={{ textAlign: isRTL ? 'left' : 'right' }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: C.textMuted, marginBottom: 4 }}>{t.playersLabel}</div>
            <div style={{ fontWeight: 900, fontSize: 28, color: atLimit ? C.gold : C.green }}>
              {players.length}{!isPaid && <span style={{ fontSize: 15, color: C.textMuted, fontWeight: 700 }}> / {FREE_LIMIT}</span>}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 12, width: '100%', maxWidth: 304, boxSizing: 'border-box' }}>
            <QRCodeSVG
              value={`https://tabliq.io?room=${roomCode}`}
              size={280}
              bgColor="#ffffff"
              fgColor="#07071A"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </div>
          <div style={{ fontSize: 11, color: C.textSub, textAlign: 'center', lineHeight: 1.6 }}>
            {t.scanQR}<br/>{t.orTypeCode}
          </div>
        </div>
      </div>

      <button
        onClick={() => window.open(`${window.location.origin}?tv=${roomCode}&lang=${lang}`, '_blank')}
        style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, color: C.white, borderRadius: 12, padding: '12px', fontSize: 13, fontWeight: 700, cursor: 'pointer', marginBottom: 16 }}
      >
        📺 Open TV screen (big display)
      </button>

      {!gameStarted && players.length === 0 && (
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px dashed rgba(255,255,255,0.12)',
          borderRadius: 10,
          padding: '12px 16px',
          textAlign: 'center',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          color: C.textMuted,
          fontSize: 13,
          fontWeight: 500,
        }}>
          <span style={{ fontSize: 16 }}>⏳</span>
          <span>{t.waitingPlayers}</span>
        </div>
      )}

      {/* Upgrade nudge — a player was turned away by the free 6-player cap */}
      {showUpgradeNudge && (
        <div style={{ background: `${C.gold}12`, border: `1px solid ${C.gold}40`, borderRadius: 12, padding: '16px', marginBottom: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 22, marginBottom: 8 }}>🔒</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.white, marginBottom: 4 }}>{gate.reached}</div>
          <div style={{ fontSize: 15, color: C.textSub, marginBottom: 14 }}>{gate.over}</div>
          <button onClick={() => onUpgrade && onUpgrade()} style={{ width: '100%', background: C.gold, color: '#000', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
            {gate.btn}
          </button>
        </div>
      )}

      {!gameStarted && players.length > 0 && (
        <button onClick={() => { setGameStarted(true); socket.emit('game-started', roomCode); }}
          style={{ width: '100%', background: C.green, color: '#000', border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 800, cursor: 'pointer', marginBottom: 16 }}>
          {t.startGame} — {players.length} {t.players}
        </button>
      )}

      {gameStarted && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 18, color: C.textMuted }}>
              Q<span style={{ color: C.gold, fontSize: 26, fontWeight: 900 }}>{qIndex + 1}</span>/{QUESTIONS.length}
            </div>
            <div style={{ background: 'rgba(255,209,102,0.15)', borderRadius: 20, padding: '5px 14px', fontSize: 13, fontWeight: 700, color: C.gold }}>
              {phase === 'reading' ? t.readingBadge : phase === 'gameover' ? t.gameOverBadge : t.buzzersOpenBadge}
            </div>
          </div>

          {phase !== 'gameover' && (
            <div style={{ background: 'rgba(255,209,102,0.07)', border: '1px solid rgba(255,209,102,0.2)', borderRadius: 14, padding: '16px', marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: C.gold, marginBottom: 10 }}>{t.questionReadOut}</div>

              {currentQ?.flag && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                  <img
                    src={`https://flagcdn.com/w320/${currentQ.flag}.png`}
                    alt=""
                    style={{ width: 150, borderRadius: 8, border: '1px solid rgba(255,255,255,0.18)', display: 'block' }}
                  />
                </div>
              )}

              {renderMultiLang(currentQ?.q, 16)}

              {!buzzed && (
                <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed rgba(255,255,255,0.15)' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: C.green, marginBottom: 6 }}>👁 ANSWER (host only)</div>
                  {renderMultiLang(currentQ?.a, 15)}
                </div>
              )}
            </div>
          )}

          {phase === 'reading' && (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '18px', textAlign: 'center', marginBottom: 14 }}>
              <div style={{ fontSize: 13, color: C.textSub, marginBottom: 16 }}>{t.readQuestionInstruction}</div>
              <button onClick={openBuzzers} style={{ width: '100%', background: C.gold, color: '#000', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
                {t.openBuzzersBtn}
              </button>
            </div>
          )}

          {phase === 'open' && !buzzed && !result && (
            <div style={{ background: 'rgba(6,214,160,0.08)', border: '1px solid rgba(6,214,160,0.25)', borderRadius: 14, padding: '20px', textAlign: 'center', marginBottom: 14 }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>🔔</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.textSub }}>{t.waitingBuzz}</div>
            </div>
          )}

          {buzzed && !result && (
            <div style={{ background: 'linear-gradient(135deg, #EF476F, #b52d50)', borderRadius: 14, padding: '14px 16px', marginBottom: 14 }}>
              <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 2 }}>🔔 {buzzed.name} {t.buzzedFirstAlert}</div>
              <div style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,209,102,0.4)', borderRadius: 10, padding: '10px 12px', marginBottom: 12, marginTop: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: C.gold, marginBottom: 6 }}>{t.correctAnswerLabel}</div>
                {renderMultiLang(currentQ?.a, 16)}
              </div>
              {buzzOrder.length > 1 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>{t.buzzOrderLabel}</div>
                  {buzzOrder.map((b, i) => (
                    <div key={i} style={{ fontSize: 12, color: '#fff', marginBottom: 3 }}>
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'} {b.name}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => validate(true)} style={{ flex: 1, background: C.green, color: '#000', border: 'none', borderRadius: 9, padding: '11px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>{t.correctBtn}</button>
                <button onClick={() => validate(false)} style={{ flex: 1, background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: 9, padding: '11px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{t.wrongBtn}</button>
              </div>
            </div>
          )}

          {result === 'correct' && (
            <div style={{ background: 'linear-gradient(135deg, #06D6A0, #04a07a)', borderRadius: 14, padding: '16px', marginBottom: 14, textAlign: 'center' }}>
              <div style={{ fontSize: 26, marginBottom: 4 }}>🎉</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#000', marginBottom: 4 }}>+100 {t.to} {buzzed?.name}!</div>
              <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.6)', marginBottom: 14 }}>{t.discussAnswer}</div>
              <button onClick={nextQuestion} style={{ width: '100%', background: 'rgba(0,0,0,0.8)', color: '#fff', border: 'none', borderRadius: 10, padding: '13px', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                {qIndex >= QUESTIONS.length - 1 ? t.seeResults : t.nextQuestionBtn}
              </button>
            </div>
          )}

          {phase === 'gameover' && (
            <div style={{ marginTop: 10 }}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 48 }}>🏆</div>
                <div style={{ fontSize: 24, fontWeight: 900, marginTop: 8 }}>{t.gameOverTitle}</div>
                <div style={{ fontSize: 13, color: C.textSub, marginTop: 4 }}>{t.finalResults}</div>
              </div>

              {sortedPlayers.slice(0, 3).map((p, i) => (
                <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 14, background: i === 0 ? `${C.gold}20` : i === 1 ? 'rgba(192,192,192,0.15)' : 'rgba(205,127,50,0.15)', border: `1px solid ${i === 0 ? C.gold : i === 1 ? '#C0C0C0' : '#CD7F32'}`, marginBottom: 10 }}>
                  <div style={{ fontSize: 28 }}>{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 900 }}>{p.name}</div>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: i === 0 ? C.gold : C.white }}>{p.pts} {t.pts}</div>
                </div>
              ))}

              {sortedPlayers.slice(3).map((p, i) => (
                <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 10, background: C.card, marginBottom: 5 }}>
                  <div style={{ fontWeight: 900, fontSize: 14, width: 20, color: C.textMuted }}>{i + 4}</div>
                  <div style={{ flex: 1, fontSize: 13 }}>{p.name}</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: C.textSub }}>{p.pts} {t.pts}</div>
                </div>
              ))}

              {!winnerRevealed ? (
                <button onClick={revealWinner} style={{ width: '100%', background: 'linear-gradient(135deg, #FFD166, #f0a500)', color: '#000', border: 'none', borderRadius: 14, padding: '16px', fontSize: 16, fontWeight: 900, cursor: 'pointer', marginTop: 16 }}>
                  {t.revealWinner}
                </button>
              ) : (
                <div style={{ marginTop: 16 }}>
                  <div style={{ background: C.green, borderRadius: 14, padding: '14px', textAlign: 'center', marginBottom: 12 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#000' }}>{t.winnerRevealedMsg}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={onBack} style={{ flex: 1, background: C.gold, color: '#000', border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                      {t.newGame}
                    </button>
                    <button onClick={() => window.location.href = '/'} style={{ flex: 1, background: 'rgba(255,255,255,0.08)', color: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                      {t.home}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {sortedPlayers.length > 0 && phase !== 'gameover' && (
            <>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: C.textMuted, marginBottom: 10, marginTop: 8 }}>{t.liveLeaderboard}</div>
              {sortedPlayers.map((p, i) => (
                <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 10, background: i === 0 ? 'rgba(255,209,102,0.09)' : C.card, marginBottom: 5 }}>
                  <div style={{ fontWeight: 900, fontSize: 15, width: 16, color: i === 0 ? C.gold : C.textMuted }}>{i + 1}</div>
                  <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                  <div style={{ fontWeight: 900, fontSize: 17, color: C.gold }}>{p.pts}</div>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default Host;